import { COUNTY_LEVEL_LAYER } from '../../services/layer-store.service';
import {
  AIR_POLLUTION,
  AIR_POLLUTION_INDEX,
  AVERAGE_ANNUAL_SPENDING,
  AVERAGE_HOUSEHOLD_SIZE,
  AVERAGE_TRAVEL_TIME_TO_WORK,
  BACHELORS_DEGREE_OR_HIGHER_FRACTION,
  BINGE_DRINKING_RATE,
  BUSINESS_CRIME_RATE,
  CANCER_PREVALENCE,
  COMBINED_HWW_BY_COUNTRY,
  COMMUNITY_CRIME_RATE,
  CRIME_RATE,
  CUMULATIVE_TRAFFIC_INTENSITY,
  DIABETES_PREVALENCE,
  GENDER_PAY_GAP,
  GINI_INDEX,
  HEALTH_INDEX_BY_COUNTRY,
  HEART_DISEASES_PREVALENCE,
  HOME_OWNERSHIP_RATIO,
  HOME_PRICE_TO_INCOME_RATIO,
  HOUSEHOLDS,
  LIFE_EXPECTANCY,
  MEDIAN_AGE,
  MEDIAN_GROSS_RENT,
  MEDIAN_GROSS_RENT_TREND,
  MEDIAN_HOUSEHOLD_INCOME_TREND,
  MEDIAN_HOUSING_SALE_COST,
  MEDIAN_HOUSING_SALE_COST_TREND,
  MEDIAN_INCOME,
  MEDIAN_NUMBER_OF_ROOMS,
  MEDIAN_TENURE,
  NUMBER_OF_VEHICLES_AVAILABLE,
  OBESITY_RATE,
  PATIENT_TO_DENTIST_RATIO,
  PATIENT_TO_PHYSICIAN_RATIO,
  PATIENT_TO_THERAPIST_RATIO,
  PEOPLE_PER_MI2,
  POPULATION,
  POPULATION_GROWTH_TREND,
  PROPERTY_CRIME_RATE,
  ROAD_DENSITY,
  SMOKING_RATE,
  SPECIAL_HEALTH_CARE_NEEDS_INDEX,
  STRUCTURE_BUILT_MEDIAN_YEAR,
  UNEMPLOYMENT_RATIO,
  UNHEALTHY_BEHAVIOUR_INDEX,
  UNINSURED_RATIO,
  VIOLENT_CRIME_RATE,
  WEALTH_INDEX_BY_COUNTRY,
  WISE_INDEX_BY_COUNTRY,
  WORK_AT_HOME,
} from '../../../../shared/types/feature-data-type';

/**
 * The popup used to render whatever `properties` happened to arrive, in the
 * order of the right menu, as one flat 40-row list. This file is the taxonomy
 * that replaced it: four subject tabs plus an overview, each tab split into
 * named sections.
 *
 * It is deliberately a static list rather than a projection of
 * `LayersMenuService.groupsData`. The menu is organised around *what you can
 * colour the map by* (and so mixes in charts, POI layers and elections); a
 * popup is read as an answer to "what kind of place is this", which wants
 * fewer, blunter buckets. Keeping them separate means re-ordering the menu
 * cannot silently re-order the popup.
 */
export type PopupIcon =
  | 'overview'
  | 'people'
  | 'business'
  | 'wealth'
  | 'health'
  | 'trends'
  | 'home'
  | 'heart';

export interface PopupSection {
  /** `null` for a tab's lead section, which needs no heading of its own. */
  label: string | null;
  features: string[];
  /** Set only on sections that draw an extra card above their rows. */
  id?: string;
  /**
   * Map levels this section has data for. Omitted means every level; listed
   * means the section is hidden everywhere else rather than printing a column
   * of N/A.
   */
  levels?: string[];
  /** Why the section is restricted, printed under its heading. */
  note?: string;
  /**
   * Keeps the section out of "Furthest from the US average". For data
   * published at a coarser level than the cell being looked at, a large
   * deviation is a property of the county, not of this place, so it would
   * crowd out highlights that actually describe the cell.
   */
  excludeFromHighlights?: boolean;
}

export interface PopupTab {
  id: string;
  label: string;
  icon: PopupIcon;
  sections: PopupSection[];
}

export const OVERVIEW_TAB_ID = 'overview';

/** Business tab section carrying the AADT card (see `TrafficSummary`). */
export const TRAFFIC_SECTION_ID = 'traffic';

/** Business tab section carrying the generation mix. */
export const CUSTOMERS_SECTION_ID = 'customers';

/** The one cohort a guest sees; the rest are the sign-up offer. */
export const FREE_GENERATION_ID = 'gen-z';

/**
 * Crime is only published per county — see the same disclaimer on the right
 * menu's Crime group in LayersMenuService. On a 1- or 5-mile cell the backend
 * returns nothing for it, and a county figure stretched over one cell would be
 * a worse answer than no answer.
 */
export const COUNTY_ONLY_FEATURES = [
  CRIME_RATE,
  VIOLENT_CRIME_RATE,
  PROPERTY_CRIME_RATE,
  COMMUNITY_CRIME_RATE,
  BUSINESS_CRIME_RATE,
];

/** Shown inside the Safety section, where the numbers are county figures. */
export const COUNTY_ONLY_NOTE =
  'Reported per county — the smallest area this data is published for.';

/**
 * Shown on the pinned row instead, when the map is coloured by one of these
 * layers somewhere they have no value. Mirrors the right menu's own wording
 * (LayersMenuService, the Crime group disclaimer) so the two agree.
 */
export const COUNTY_ONLY_UNAVAILABLE_NOTE =
  'Crime data is published only at the county level.';

/** The three component indices shown under the hero grade, in reading order. */
export const HERO_SUB_INDICES = [
  HEALTH_INDEX_BY_COUNTRY,
  WEALTH_INDEX_BY_COUNTRY,
  WISE_INDEX_BY_COUNTRY,
];

/** The combined score, always compared against the country rather than the state. */
export const HERO_SCORE = COMBINED_HWW_BY_COUNTRY;

/**
 * The four numbers on the overview, so the card answers "who, how rich, how
 * healthy, how expensive" before anything is clicked. Business has no tile of
 * its own: it re-reads these same numbers rather than adding any.
 */
export const GLANCE_TILES: { feature: string; icon: PopupIcon }[] = [
  { feature: POPULATION, icon: 'people' },
  { feature: MEDIAN_INCOME, icon: 'wealth' },
  { feature: MEDIAN_HOUSING_SALE_COST, icon: 'home' },
  { feature: LIFE_EXPECTANCY, icon: 'heart' },
];

/** Short tile captions — the full names are too long for a 4-up grid. */
export const GLANCE_LABELS: { [feature: string]: string } = {
  [POPULATION]: 'Population',
  [MEDIAN_INCOME]: 'Income',
  [MEDIAN_HOUSING_SALE_COST]: 'Home value',
  [LIFE_EXPECTANCY]: 'Longevity',
};

export const POPUP_TABS: PopupTab[] = [
  {
    id: 'people',
    label: 'People',
    icon: 'people',
    sections: [
      {
        label: null,
        features: [
          POPULATION,
          PEOPLE_PER_MI2,
          HOUSEHOLDS,
          MEDIAN_AGE,
          AVERAGE_HOUSEHOLD_SIZE,
          BACHELORS_DEGREE_OR_HIGHER_FRACTION,
        ],
      },
      {
        label: 'Getting around',
        features: [
          AVERAGE_TRAVEL_TIME_TO_WORK,
          WORK_AT_HOME,
          NUMBER_OF_VEHICLES_AVAILABLE,
          CUMULATIVE_TRAFFIC_INTENSITY,
          ROAD_DENSITY,
        ],
      },
    ],
  },
  {
    id: 'wealth',
    label: 'Wealth',
    icon: 'wealth',
    sections: [
      {
        label: null,
        features: [
          // The tab's own summary, and the same grade the score block prints
          // as "Income". First, because it is what the rows under it add up
          // to - and it keeps the answer on screen once the score block has
          // scrolled away with the overview.
          WEALTH_INDEX_BY_COUNTRY,
          MEDIAN_INCOME,
          AVERAGE_ANNUAL_SPENDING,
          UNEMPLOYMENT_RATIO,
          GINI_INDEX,
          GENDER_PAY_GAP,
        ],
      },
      {
        label: 'Housing',
        features: [
          MEDIAN_HOUSING_SALE_COST,
          MEDIAN_GROSS_RENT,
          HOME_OWNERSHIP_RATIO,
          HOME_PRICE_TO_INCOME_RATIO,
          MEDIAN_NUMBER_OF_ROOMS,
          STRUCTURE_BUILT_MEDIAN_YEAR,
          MEDIAN_TENURE,
        ],
      },
    ],
  },
  /**
   * A lens, not a new bucket of data: every row here already lives under
   * People, Wealth or Health. Site selection is the question this product is
   * actually bought for, and answering "would a business work here" by
   * scrolling three other tabs and translating each number is the work the
   * tab does for the reader. Ordered the way that question is asked - who
   * passes by, who would buy, who could be hired, what could go wrong.
   */
  {
    id: 'business',
    label: 'Business',
    icon: 'business',
    sections: [
      {
        id: TRAFFIC_SECTION_ID,
        label: 'Traffic',
        features: [
          CUMULATIVE_TRAFFIC_INTENSITY,
          ROAD_DENSITY,
          NUMBER_OF_VEHICLES_AVAILABLE,
          // Stands in for daytime population: where people work from home the
          // resident count is also the count on the street at noon.
          WORK_AT_HOME,
        ],
      },
      {
        id: CUSTOMERS_SECTION_ID,
        label: 'Customers',
        features: [
          POPULATION,
          HOUSEHOLDS,
          PEOPLE_PER_MI2,
          MEDIAN_INCOME,
          AVERAGE_ANNUAL_SPENDING,
          // Read here as customer type: owners stay, renters turn over.
          HOME_OWNERSHIP_RATIO,
        ],
      },
      {
        label: 'Workforce',
        features: [
          UNEMPLOYMENT_RATIO,
          BACHELORS_DEGREE_OR_HIGHER_FRACTION,
          MEDIAN_AGE,
        ],
      },
      {
        label: 'Risk',
        features: [BUSINESS_CRIME_RATE, PROPERTY_CRIME_RATE],
        levels: [COUNTY_LEVEL_LAYER],
        note: COUNTY_ONLY_NOTE,
        excludeFromHighlights: true,
      },
    ],
  },
  {
    id: 'health',
    label: 'Health',
    icon: 'health',
    sections: [
      {
        label: null,
        features: [
          // Same idea as the Income index on Wealth: the tab's summary first.
          HEALTH_INDEX_BY_COUNTRY,
          LIFE_EXPECTANCY,
          UNINSURED_RATIO,
          UNHEALTHY_BEHAVIOUR_INDEX,
          SPECIAL_HEALTH_CARE_NEEDS_INDEX,
          OBESITY_RATE,
          SMOKING_RATE,
          BINGE_DRINKING_RATE,
          DIABETES_PREVALENCE,
          HEART_DISEASES_PREVALENCE,
          CANCER_PREVALENCE,
        ],
      },
      {
        label: 'Care & environment',
        features: [
          PATIENT_TO_PHYSICIAN_RATIO,
          PATIENT_TO_DENTIST_RATIO,
          PATIENT_TO_THERAPIST_RATIO,
          AIR_POLLUTION_INDEX,
          AIR_POLLUTION,
        ],
      },
      {
        label: 'Safety',
        features: COUNTY_ONLY_FEATURES,
        levels: [COUNTY_LEVEL_LAYER],
        note: COUNTY_ONLY_NOTE,
        excludeFromHighlights: true,
      },
    ],
  },
  {
    id: 'trends',
    label: 'Trends',
    icon: 'trends',
    sections: [
      {
        label: null,
        features: [
          POPULATION_GROWTH_TREND,
          MEDIAN_HOUSEHOLD_INCOME_TREND,
          MEDIAN_HOUSING_SALE_COST_TREND,
          MEDIAN_GROSS_RENT_TREND,
        ],
      },
    ],
  },
];

/** Every metric the tabs can show, once each, in reading order. */
export const POPUP_TAB_FEATURES: string[] = [
  ...new Set(
    POPUP_TABS.flatMap((tab) =>
      tab.sections.flatMap((section) => section.features)
    )
  ),
];

/**
 * Which direction of a deviation from the national value counts as good news.
 *
 * Only listed where the answer is the same for every visitor. A high median
 * home value is good if you are selling and bad if you are buying, so housing
 * prices, density, age and household size stay unlisted and render neutral —
 * colouring them would be an opinion the data does not support.
 */
const HIGHER_IS_BETTER: string[] = [
  LIFE_EXPECTANCY,
  MEDIAN_INCOME,
  BACHELORS_DEGREE_OR_HIGHER_FRACTION,
  HOME_OWNERSHIP_RATIO,
  MEDIAN_HOUSEHOLD_INCOME_TREND,
];

const HIGHER_IS_WORSE: string[] = [
  CRIME_RATE,
  VIOLENT_CRIME_RATE,
  PROPERTY_CRIME_RATE,
  COMMUNITY_CRIME_RATE,
  BUSINESS_CRIME_RATE,
  UNEMPLOYMENT_RATIO,
  UNINSURED_RATIO,
  GINI_INDEX,
  GENDER_PAY_GAP,
  UNHEALTHY_BEHAVIOUR_INDEX,
  SPECIAL_HEALTH_CARE_NEEDS_INDEX,
  OBESITY_RATE,
  SMOKING_RATE,
  BINGE_DRINKING_RATE,
  DIABETES_PREVALENCE,
  CANCER_PREVALENCE,
  HEART_DISEASES_PREVALENCE,
  AIR_POLLUTION,
  AIR_POLLUTION_INDEX,
  PATIENT_TO_PHYSICIAN_RATIO,
  PATIENT_TO_DENTIST_RATIO,
  PATIENT_TO_THERAPIST_RATIO,
  HOME_PRICE_TO_INCOME_RATIO,
  AVERAGE_TRAVEL_TIME_TO_WORK,
];

export type Polarity = 'good' | 'bad' | 'neutral';

/** Reads how an above-national value should be judged for `feature`. */
export function polarityOf(feature: string, isAbove: boolean): Polarity {
  const key = feature.toUpperCase();
  const better = HIGHER_IS_BETTER.some((f) => f.toUpperCase() === key);
  const worse = HIGHER_IS_WORSE.some((f) => f.toUpperCase() === key);

  if (!better && !worse) return 'neutral';
  if (better) return isAbove ? 'good' : 'bad';
  return isAbove ? 'bad' : 'good';
}
